import { OpstartplanActie, useReplaceActie } from "@/entities/actie";
import { useOpstartplanActieDialogContext } from "@/features/acties/components/dialogs/opstartplan-actie-dialog/opstartplan-actie-dialog-context.ts";
import { OpstartplanActieDialogProvider } from "@/features/acties/components/dialogs/opstartplan-actie-dialog/opstartplan-actie-dialog-provider.tsx";
import { Treinseries } from "@/features/opstartplan";
import { Button, DialogFooter, ErrorAlert, ErrorDisplay } from "@/shared/components";
import { isApiError } from "@/shared/utils";
import { useState } from "react";
import { ActieDialog, DeclineActieConfirmation } from "../actie-dialog.tsx";

type OpstartplanActieDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dossierId: string;
    actie: OpstartplanActie;
};

export function OpstartplanActieDialog({ open, onOpenChange, dossierId, actie }: Readonly<OpstartplanActieDialogProps>) {
    if (actie.wijzigingType === "Toegevoegd" || actie.wijzigingType === "Gewijzigd") {
        return (
            <OpstartplanActieDialogProvider actie={actie}>
                <OpstartplanActieDialogContainer dossierId={dossierId} actie={actie} open={open} onOpenChange={onOpenChange} />
            </OpstartplanActieDialogProvider>
        );
    }

    return null;
}

function OpstartplanActieDialogContainer({ open, onOpenChange, dossierId, actie }: OpstartplanActieDialogProps) {
    const opstartplanActieContext = useOpstartplanActieDialogContext();

    return (
        <ActieDialog
            open={open}
            onOpenChange={(open) => {
                if (!open) {
                    opstartplanActieContext.resetAll();
                }
                onOpenChange(open);
            }}
            dossierId={dossierId}
            actie={actie}
            footer={OpstartplanDialogFooter}
        >
            <Treinseries
                actionsEnabled
                treinseries={opstartplanActieContext.actie.nieuweWaarde}
                onEdit={opstartplanActieContext.updateTreinnummer}
                onDelete={opstartplanActieContext.deleteTreinnummer}
            />
        </ActieDialog>
    );
}

type OpstartplanDialogFooterProps = {
    dossierId: string;
    onClose: () => void;
    className?: string;
};

function OpstartplanDialogFooter({ className, dossierId, onClose }: Readonly<OpstartplanDialogFooterProps>) {
    const { actie } = useOpstartplanActieDialogContext();
    const { mutate: replaceActie, error, isPending } = useReplaceActie();
    const [toelichting, setToelichting] = useState("");
    const [weigerenPopupOpen, setWeigerenPopupOpen] = useState(false);

    const isBadRequestError = error && isApiError(error) && error.status === 400;

    const hasTreinnummersWithoutVertrektijd = actie.nieuweWaarde.some((ts) =>
        ts.treinnummers.some((tn) => tn.vertrekDatumTijd === undefined)
    );

    const handleAcceptAction = () => {
        replaceActie(
            {
                dossierId,
                actieId: actie.id,
                actie: OpstartplanActie.toDto(actie, toelichting),
            },
            { onSuccess: () => onClose() }
        );
    };

    return (
        <div className={className}>
            <div className="px flex flex-col gap-2 px-6 pb-8">
                <ActieDialog.Toelichting
                    toelichtingLabel={actie.toelichtingLabel}
                    toelichting={toelichting}
                    setToelichting={setToelichting}
                />
            </div>
            <DialogFooter className="grid justify-stretch gap-2">
                <div className="flex w-full items-center justify-end gap-2">
                    <Button
                        data-testid="decline-actie"
                        variant="tertiary"
                        destructive
                        onClick={() => setWeigerenPopupOpen(true)}
                        disabled={isPending}
                    >
                        Weigeren
                    </Button>
                    <Button
                        data-testid="confirm-actie"
                        variant="primary"
                        onClick={handleAcceptAction}
                        disabled={isPending || hasTreinnummersWithoutVertrektijd}
                    >
                        Bevestigen
                    </Button>
                </div>
                {isBadRequestError && (
                    <ErrorAlert description="Het opstartplan is incompleet, controleer de treinseries en probeer opnieuw" />
                )}
                {!isBadRequestError && error && <ErrorDisplay error={error} />}
                <DeclineActieConfirmation
                    actie={actie}
                    isOpen={weigerenPopupOpen}
                    setIsOpen={setWeigerenPopupOpen}
                    closeActieDialog={onClose}
                    dossierId={dossierId}
                />
            </DialogFooter>
        </div>
    );
}
